import { Badge } from '@cloudflare/kumo'
import type { PrinterStatus } from '../api/printers'
import { useTranslation } from '../i18n'

/**
 * Single source of truth for printer-status visual presentation. Maps a
 * PrinterStatus to a Kumo `<Badge>` with i18n label.
 *
 * Kumo `Badge` exposes a fixed variant set (`primary`, `secondary`,
 * `success`, `warning`, `danger`, ...). We map our 5 statuses onto that
 * set rather than introduce custom Tailwind classes — keeps light/dark
 * mode + design-token consistency for free.
 *
 * Color vocabulary (from design doc "Admin UI"):
 *   pending_oauth         warning   — awaiting OAuth
 *   pending_verification  warning   — tokens valid, awaiting test print
 *   active                success   — user-visible from here
 *   inactive              neutral   — admin deactivated
 *   revoked               error     — re-onboard required
 */

interface Props {
  status: PrinterStatus
}

type KumoBadgeVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'neutral'

const STATUS_VARIANT: Record<PrinterStatus, KumoBadgeVariant> = {
  pending_oauth: 'warning',
  pending_verification: 'warning',
  active: 'success',
  inactive: 'neutral',
  revoked: 'error',
}

export function PrinterStatusBadge({ status }: Props) {
  const { t } = useTranslation()
  const label = t(`printers.status.${status}`)
  return (
    <Badge variant={STATUS_VARIANT[status]} aria-label={label}>
      {label}
    </Badge>
  )
}
